/**
 * Predicate-family registry for the scope-filter builder (SMT-1608 Phase 1).
 *
 * Each path in scopeFilterCatalog.js declares a `type` (one of the keys
 * below). The type family determines:
 *   - which operators (`$type` discriminators) are valid
 *   - the value-input shape the UI renders
 *   - how the value serializes onto the wire
 *
 * Wire-format reference: every predicate is a JSON object with a `$type`
 * discriminator + `path` + a value-shape specific payload:
 *   - `haystack: FilterResult[]`  for equal/not-equal (set membership)
 *   - `needles:  FilterResult[]`  for contains/starts-with/ends-with (substring)
 *   - `lower:    FilterResult`    for greater-than(-equal)
 *   - `upper:    FilterResult`    for less-than(-equal)
 *   - `lower + upper`             for range (inclusive on both sides)
 *   - `value:    FilterResult`    for bool equal/not-equal
 *
 * `FilterResult` is normally a literal (string / number / bool / ISO date)
 * but Orion accepts calculation references too (`{ $accessor: '<path>' }`).
 * The v1 UI surfaces literals only; advanced cross-calc comparisons are
 * reachable via the free-text path-expression escape hatch (deferred).
 */

const stringOps = [
  { $type: 'string-equal', label: 'is', valueShape: 'haystack' },
  { $type: 'string-not-equal', label: 'is not', valueShape: 'haystack' },
  { $type: 'string-contains', label: 'contains', valueShape: 'needles' },
  { $type: 'string-not-contains', label: 'does not contain', valueShape: 'needles' },
  { $type: 'string-starts-with', label: 'starts with', valueShape: 'needles' },
  { $type: 'string-ends-with', label: 'ends with', valueShape: 'needles' }
]

// Compares share an identical operator shape across numeric / decimal /
// currency / percent / date / datetime; only the `$type` prefix differs.
function buildCompareOps (prefix, { range = true } = {}) {
  const ops = [
    { $type: `${prefix}-equal`, label: 'equals', valueShape: 'single' },
    { $type: `${prefix}-not-equal`, label: 'not equals', valueShape: 'single' },
    { $type: `${prefix}-less-than`, label: '<', valueShape: 'upper' },
    { $type: `${prefix}-less-than-equal`, label: '<=', valueShape: 'upper' },
    { $type: `${prefix}-greater-than`, label: '>', valueShape: 'lower' },
    { $type: `${prefix}-greater-than-equal`, label: '>=', valueShape: 'lower' }
  ]
  if (range) ops.push({ $type: `${prefix}-range`, label: 'between', valueShape: 'range' })
  return ops
}

const boolOps = [
  { $type: 'bool-equal', label: 'is', valueShape: 'single' },
  { $type: 'bool-not-equal', label: 'is not', valueShape: 'single' }
]

export const PREDICATE_FAMILIES = {
  string: { operators: stringOps, defaultValue: () => ({ haystack: [] }) },
  numeric: { operators: buildCompareOps('numeric'), defaultValue: () => ({}) },
  decimal: { operators: buildCompareOps('decimal'), defaultValue: () => ({}) },
  currency: { operators: buildCompareOps('currency'), defaultValue: () => ({}) },
  percent: { operators: buildCompareOps('percent'), defaultValue: () => ({}) },
  date: { operators: buildCompareOps('date'), defaultValue: () => ({}) },
  datetime: { operators: buildCompareOps('datetime'), defaultValue: () => ({}) },
  bool: { operators: boolOps, defaultValue: () => ({ value: false }) }
}

/**
 * Reverse lookup: given a predicate `$type`, return the type family + the
 * operator entry. Used by the builder to render the right value control
 * when round-tripping a filter loaded from saved view storage.
 */
export const OPERATORS_BY_TYPE = (() => {
  const out = {}
  for (const [familyName, family] of Object.entries(PREDICATE_FAMILIES)) {
    for (const op of family.operators) {
      out[op.$type] = { family: familyName, operator: op }
    }
  }
  return out
})()

/**
 * Preset filters that map to Orion's GroupingFilter [Flags] enum at request
 * build time. The editor surfaces these as a non-editable chip; the
 * compiler in scopeFilterCompile.js expands them into the equivalent wire
 * form (bare string when alone, or currency-not-equal predicate when
 * composed with other filters).
 */
export const FILTER_PRESETS = [
  {
    preset: 'HasValue',
    label: 'Hide rows with zero ending market value',
    description: 'Excludes groups whose Activity.Value is null or zero.'
  },
  {
    preset: 'HasUnits',
    label: 'Hide rows with no units',
    description: 'Excludes groups whose Activity.EndUnits is null or zero.'
  }
]

export const FILTER_PRESETS_BY_KEY = Object.fromEntries(
  FILTER_PRESETS.map(p => [p.preset, p])
)
